import { format, isValid, parseISO } from 'date-fns';

import type { Period, TermedLevel } from '../../common/termedLevel/types';
import { type BookingOverviewStatus, getBookingOverviewStatus } from './bookingOverviewStatus';

export type BookingOverviewFilters = {
    resources: string[];
    periods: string[];
    statuses: BookingOverviewStatus[];
    freeOnly: boolean;
    withoutRenewal: boolean;
    endsFrom?: string;
    endsTo?: string;
};

export const emptyBookingOverviewFilters: BookingOverviewFilters = {
    resources: [],
    periods: [],
    statuses: [],
    freeOnly: false,
    withoutRenewal: false,
};

export const getBookingOverviewResourceKey = (contract: TermedLevel): string =>
    JSON.stringify([contract.resourceType, contract.resourceId]);

function getContractPeriod(contract: TermedLevel): (Period & { id: string }) | undefined {
    if (contract.periodLength === undefined || contract.periodUnit === undefined) {
        return;
    }
    return {
        id: `${contract.periodLength}:${contract.periodUnit}`,
        length: contract.periodLength,
        unit: contract.periodUnit,
    };
}

export function getBookingOverviewPeriods(contracts: TermedLevel[]): (Period & { id: string })[] {
    const periods = new Map<string, Period & { id: string }>();
    for (const contract of contracts) {
        const period = getContractPeriod(contract);
        if (period) {
            periods.set(period.id, period);
        }
    }
    return [...periods.values()].sort((a, b) => a.unit.localeCompare(b.unit) || a.length - b.length);
}

export function filterBookingOverviewContracts(
    contracts: TermedLevel[],
    filters: BookingOverviewFilters,
    search = ''
): TermedLevel[] {
    const query = search.trim().toLocaleLowerCase();
    return contracts.filter(contract => {
        if (filters.resources.length && !filters.resources.includes(getBookingOverviewResourceKey(contract))) {
            return false;
        }
        const searchableValues = [contract.resourceName, contract.resourceId];
        if (contract.resourceType !== 'user') {
            searchableValues.push(contract.resourceLicensePlate, contract.resourceVin);
        }
        if (query && !searchableValues.some(value => value?.toLocaleLowerCase().includes(query))) {
            return false;
        }
        if (filters.periods.length && !filters.periods.includes(getContractPeriod(contract)?.id ?? '')) {
            return false;
        }
        if (filters.statuses.length && !filters.statuses.includes(getBookingOverviewStatus(contract))) {
            return false;
        }
        if (filters.freeOnly && contract.paid !== false) {
            return false;
        }
        if (filters.withoutRenewal && contract.renewsToLevel !== undefined) {
            return false;
        }
        if (filters.endsFrom || filters.endsTo) {
            const endDate = contract.endsAt ? parseISO(contract.endsAt) : undefined;
            if (!endDate || !isValid(endDate)) {
                return false;
            }
            const endDay = format(endDate, 'yyyy-MM-dd');
            return (!filters.endsFrom || endDay >= filters.endsFrom) && (!filters.endsTo || endDay <= filters.endsTo);
        }
        return true;
    });
}
